// 개발요청 — 새 요청 / 고치기 창(D4b · 부서 + ① 6칸 + 공개 범위 읽기 전용 표시).
// 막는 것은 서버다(routers/devreq.py create_request · update_request) — 400·409 문구는 창을 닫지 않고 창 안에 보인다.
// 🔴 공개 범위는 관리자(AX)가 정한다 — 이 창은 표시만 하고 저장 몸통에 visibility 를 넣지 않는다(서버가 400).
// 🔴 고치기는 바뀐 칸만 보낸다(키 없음 = 안 바꿈 · 서버도 보낸 칸만 본다).
// 남은 건수·순위 범위는 그 (회차, 부서)의 목록 응답으로 센다 — 서버가 이 숫자를 따로 주는 곳은 없다.
// 칸별 첨부(2판 V2): «지금 하는 방식»·«원하는 기능»·«참고자료» 아래 첨부 줄(DevRequestAttachField) — 저장할 때
//   ① 글 저장(create · 바뀐 칸이 있을 때만 update) → ② ✕ 한 기존 첨부 지우기 → ③ 새 파일을 칸 이름(field)과 하나씩 올리기 → ④ onSaved.
//   ②·③ 이 하나라도 실패하면 창을 닫지 않는다(실패한 칩만 남는다 · 다시 «저장» 해도 create 를 다시 부르지 않는다).
//   무엇이든 서버에 들어간 뒤에는 «닫기»·Esc·바깥 누르기도 onSaved 로 닫는다(목록·상세가 다시 읽는다).
//   응답 없이 끊긴 실패(err.response 없음)도 서버는 처리했을 수 있으니 같다 — 4xx(서버가 거절)는 아니다(2판 V3 §0-1).
//   onSaved 는 창 하나에서 많아야 한 번(notifiedRef) · 썸네일은 상세가 이미 받은 것(detailThumbs)을 다시 받지 않는다.
import { useEffect, useRef, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { COLORS, RADIUS, btn, input as inputStyle } from '../../utils/uiStyles';
import DevRequestAttachField, { dropHasFiles, useFileThumbs } from './DevRequestAttachField';
import { errorText } from './devreqLabels';

// ① 글자 칸 — 서버 devreq_rules.PART1_TEXT 와 같은 길이(참고자료만 비워도 된다). multiline 칸 셋은 첨부 칸이기도 하다.
const TEXT_FIELDS = [
  { name: 'title', label: '제목', max: 200, required: true, multiline: false, placeholder: '예: 재고 엑셀 자동 집계' },
  {
    name: 'current_way', label: '지금 하는 방식', max: 1000, required: true, multiline: true,
    placeholder: '예: 매일 아침 WMS 에서 재고를 엑셀로 받아 센터별 합계를 손으로 낸다(약 40분)',
  },
  {
    name: 'wanted_feature', label: '원하는 기능', max: 1000, required: true, multiline: true,
    placeholder: '예: 센터별 재고 합계가 자동으로 집계되어 아침에 메신저로 받아 보면 좋겠다',
  },
  { name: 'target_users', label: '사용 대상·빈도', max: 200, required: true, multiline: false, placeholder: '예: 재고관리 담당 5명 · 매일' },
  { name: 'reference_note', label: '참고자료', max: 1000, required: false, multiline: true, placeholder: '관련 파일 위치·화면 이름·링크 등' },
];
// 서버 devreq_rules.PART1_FIELDS 와 같은 칸(visibility 없음).
const FIELDS = [...TEXT_FIELDS.map((f) => f.name), 'dept_rank'];
// 첨부 칸 — 서버 devreq_rules.FILE_FIELDS 중 ① 글 칸 셋.
const ATTACH_FIELDS = TEXT_FIELDS.filter((f) => f.multiline).map((f) => f.name);
const emptyStaged = () => Object.fromEntries(ATTACH_FIELDS.map((name) => [name, []]));
const SAVE_FAILED = '저장하지 못했습니다.';
const UPLOAD_FAILED = '파일을 올리지 못했습니다.';
const DELETE_FAILED = '파일을 지우지 못했습니다.';
const FORM_GUIDE = '무엇이 불편한지와 원하는 결과를 적어 주세요. 캡처를 붙이면 검토가 빨라져요.';
const FILES_LOADING = '첨부 목록을 불러오는 중…';
const FILES_LOAD_FAILED = '첨부 목록을 불러오지 못했습니다. 글은 저장할 수 있습니다.';
const RANK_LOCKED = '추가 요청에 답하는 동안에는 순위를 바꿀 수 없습니다.';

const fieldId = (name) => `devreq-field-${name}`;
// 글자 수 «1,000» — 실행 환경의 로캘과 무관하게 같은 꼴.
const commas = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const labelStyle = { display: 'block', marginBottom: '6px', color: COLORS.text, fontSize: '13px', fontWeight: 700 };
const boxStyle = { marginBottom: '12px', minWidth: 0 };
const noteStyle = { marginTop: '4px', color: COLORS.textSub, fontSize: '12px' };
const uniq = (list) => [...new Set(list)];

// 상세 응답 → 칸 글자(null 은 빈 글자) · 순위는 '' = 미정.
function initialOf(item) {
  const values = {};
  TEXT_FIELDS.forEach(({ name }) => {
    const raw = item?.[name];
    values[name] = raw === null || raw === undefined ? '' : String(raw);
  });
  values.dept_rank = item?.dept_rank === null || item?.dept_rank === undefined ? '' : String(item.dept_rank);
  return values;
}
// 저장 뒤 기준선 — 응답(그 요청)이 있으면 응답 값, 없으면 보낸 값(다시 저장할 때 같은 칸이 또 나가지 않게).
const baselineOf = (data, sent) => (data && data.id !== undefined && data.id !== null ? initialOf(data) : { ...sent });

// 보낼 값 · 비교 값 — 글자는 앞뒤 공백을 뗀다(서버도 뗀다) · 순위는 정수 또는 null(미정).
const wire = (name, value) => (name === 'dept_rank'
  ? (value === '' ? null : Number(value))
  : String(value ?? '').trim());

function visibilityText(visibility) {
  if (visibility === 'private') return '공개 범위: 비공개(관리자가 정합니다)';
  if (visibility === 'public') return '공개 범위: 공개';
  return '공개 범위: —';
}

export default function DevRequestForm({ mode, item, me, round, departments, detailThumbs, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const myDepts = Array.isArray(me?.departments) ? me.departments : [];
  const initialRef = useRef(null);
  if (initialRef.current === null) initialRef.current = initialOf(isEdit ? item : null);
  const [values, setValues] = useState(() => ({ ...initialRef.current }));
  const [deptChoice, setDeptChoice] = useState(() => String(myDepts[0]?.id ?? ''));
  const [deptItems, setDeptItems] = useState(null);   // null = 불러오는 중 · 실패해도 null(숫자를 안 보인다)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // 첨부 — 서버에 이미 있는 것(고치기 · 저장 뒤 올라간 것) · 칸별로 새로 고른 것 · ✕ 한(지울) 기존 첨부 id.
  const [serverFiles, setServerFiles] = useState([]);
  const [filesState, setFilesState] = useState(isEdit ? 'loading' : 'ready');   // 'loading' | 'ready' | 'failed'
  const [staged, setStaged] = useState(emptyStaged);
  const [removals, setRemovals] = useState([]);
  // 새 요청을 만든 뒤의 id(다시 저장해도 create 를 다시 부르지 않는다) · 무엇이든 서버에 들어간 뒤의 최신 요청.
  const [savedId, setSavedId] = useState(null);
  const [committed, setCommitted] = useState(null);
  const savingRef = useRef(false);
  const notifiedRef = useRef(false);   // onSaved 를 이미 불렀다(그 뒤 닫기는 아무것도 부르지 않는다)
  const mountedRef = useRef(true);
  const firstFieldRef = useRef(null);
  const areaRefs = {
    current_way: useRef(null),
    wanted_feature: useRef(null),
    reference_note: useRef(null),
  };

  // 부서 — 고치기는 그 요청의 부서(바꿀 수 없다) · 새 요청은 내 부서 중 고른 것(만든 뒤에는 잠근다).
  const deptId = isEdit ? item?.dept_id : (Number(deptChoice) || null);
  const roundId = isEdit ? item?.round_id : round?.id;

  useEffect(() => {
    mountedRef.current = true;
    firstFieldRef.current?.focus();
    return () => { mountedRef.current = false; };
  }, []);

  // 고치기 — 그 요청의 첨부(칸별로 나눠 기존 칩으로 보인다 · 칸이 없거나 ask_answer 인 것은 칸에 안 보이지만 상한에 센다).
  useEffect(() => {
    if (!isEdit || item?.id === undefined || item?.id === null) return undefined;
    let alive = true;
    devreqAPI.files(item.id)
      .then((r) => {
        if (!alive) return;
        setServerFiles(Array.isArray(r?.data) ? r.data : []);
        setFilesState('ready');
      })
      .catch(() => { if (alive) setFilesState('failed'); });
    return () => { alive = false; };
  }, []);

  // 그 (회차, 부서)의 요청 — 남은 건수·순위 범위용.
  useEffect(() => {
    setDeptItems(null);
    if (roundId === undefined || roundId === null || !deptId) return undefined;
    let alive = true;
    devreqAPI.list({ round_id: roundId, dept_id: deptId })
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r?.data?.items) ? r.data.items : [];
        setDeptItems(list.filter((it) => it.dept_id === deptId && it.round_id === roundId));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [roundId, deptId]);

  const setField = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));
  const changed = FIELDS.filter((name) => wire(name, values[name]) !== wire(name, initialRef.current[name]));
  const requiredFilled = TEXT_FIELDS.every(({ name, required }) => !required || wire(name, values[name]) !== '');
  const stagedTotal = ATTACH_FIELDS.reduce((sum, name) => sum + staged[name].length, 0);
  const canSave = requiredFilled && !!deptId && !saving
    && (!isEdit || changed.length > 0 || stagedTotal > 0 || removals.length > 0);

  // 상한 계산 — 서버의 살아 있는 첨부 − 지울 예정 + 고른 것(칸 구분 없이 합산 · 서버 MAX_FILES).
  const removalSet = new Set(removals);
  const liveServer = serverFiles.filter((f) => !removalSet.has(f.id));
  const existingOf = (name) => liveServer.filter((f) => f.field === name);
  const otherCountOf = (name) => liveServer.length - existingOf(name).length + (stagedTotal - staged[name].length);
  // 썸네일 — 상세가 이미 받은 것(detailThumbs)은 그 주소를 쓰고, 상세가 아직 못 받은 것만 이 창이 받는다.
  const givenThumbs = detailThumbs || {};
  const ownThumbs = useFileThumbs(serverFiles.filter((f) => ATTACH_FIELDS.includes(f.field) && !givenThumbs[f.id]));
  const thumbs = { ...givenThumbs, ...ownThumbs };
  const attachOff = saving || filesState !== 'ready';

  const stageFiles = (name, list) => setStaged((prev) => ({ ...prev, [name]: [...prev[name], ...list] }));
  const unstage = (name, index) => setStaged((prev) => ({ ...prev, [name]: prev[name].filter((_, i) => i !== index) }));
  const markRemoval = (id) => setRemovals((prev) => (prev.includes(id) ? prev : [...prev, id]));

  // 건수 상한 = 부서 cap_override ?? 회차 per_dept_cap(서버 devreq_rules.cap_of).
  const deptRow = (Array.isArray(departments) ? departments : []).find((d) => d.id === deptId);
  const cap = Number.isInteger(deptRow?.cap_override) ? deptRow.cap_override : (Number(round?.per_dept_cap) || 0);
  const live = (deptItems || []).filter((it) => !it.withdrawn);
  // 남은 건수 — 철회 안 되고 이월이 아닌 건(서버 건수 상한도 이월 건을 세지 않는다).
  const capCount = live.filter((it) => !it.carried).length;
  // 순위 범위 = max(상한, 철회 안 된 건수[이월 포함]) — 새 요청은 만들 1건을 더한다(서버 _rank_limit 의 adding=1).
  const rankLimit = Math.max(cap, live.length + (isEdit ? 0 : 1), Number(values.dept_rank) || 0);
  const rankOptions = Array.from({ length: rankLimit }, (_, i) => i + 1);
  // 추가 요청에 답하는 중이면 순위를 잠근다 — 서버는 답하는 갈래(접수 기간 밖의 부서 작성자)에서만 dept_rank 를 403 으로 막는다.
  //   AX 와 접수 기간 안(회차 open · 상태 접수)의 작성자는 추가 요청 중에도 순위를 바꾼다(devreq_rules.can_edit_part1_base).
  const rankLocked = isEdit && item?.open_ask === true && me?.is_ax !== true
    && !(round?.status === 'open' && item?.status === '접수');

  const visibility = isEdit ? item?.visibility : round?.default_visibility;

  // onSaved 는 창 하나에서 많아야 한 번 — 저장 성공으로 알린 뒤의 닫기·Esc 가 또 부르지 않는다.
  const notifySaved = (saved) => {
    if (notifiedRef.current) return;
    notifiedRef.current = true;
    onSaved?.(saved);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (savingRef.current || !canSave) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    const sent = { ...values };
    let latest = committed || (isEdit ? item : null);
    let targetId = savedId ?? (isEdit ? item.id : null);
    let wrote = false;
    let lost = false;   // 응답 없이 끊긴 지우기·올리기가 있었다(서버는 처리했을 수 있다)
    try {
      // ① 글 — 처음이면 create · 그 뒤에는 바뀐 칸이 있을 때만 update.
      try {
        if (targetId === null) {
          const body = { dept_id: deptId };
          TEXT_FIELDS.forEach(({ name }) => {
            const value = wire(name, values[name]);
            if (value) body[name] = value;
          });
          const rank = wire('dept_rank', values.dept_rank);
          if (rank !== null) body.dept_rank = rank;
          const r = await devreqAPI.create(body);
          latest = r?.data || null;
          targetId = latest?.id ?? null;
          if (targetId === null) {   // id 가 없으면 첨부를 올릴 곳이 없다 — 예전처럼 응답만 넘긴다
            if (mountedRef.current) notifySaved(latest);
            return;
          }
          initialRef.current = baselineOf(latest, sent);
          if (mountedRef.current) {
            setSavedId(targetId);
            setCommitted(latest);
          }
        } else if (changed.length > 0) {
          const body = {};
          changed.forEach((name) => { body[name] = wire(name, values[name]); });
          const r = await devreqAPI.update(targetId, body);
          latest = r?.data || latest;
          initialRef.current = baselineOf(r?.data, sent);
          wrote = true;
        }
      } catch (err) {
        const message = await errorText(err, SAVE_FAILED);
        if (mountedRef.current) {
          // 응답 없이 끊겼으면 서버는 글을 저장했을 수 있다 — 닫기가 onSaved(다시 읽기)로 가게 한다.
          //   만들기가 끊긴 것은 세울 요청(latest)이 없다 · 4xx 는 서버가 거절한 것이라 세우지 않는다.
          if (!err?.response && latest) setCommitted(latest);
          setError(message);
        }
        return;
      }

      // ② ✕ 한 기존 첨부 지우기 → ③ 새 파일을 칸 이름과 하나씩 올리기(실패한 것만 남긴다).
      const removedIds = [];
      const removeMessages = [];
      for (const id of removals) {
        if (!mountedRef.current) return;
        try {
          await devreqAPI.removeFile(id);
          removedIds.push(id);
        } catch (err) {
          if (!err?.response) lost = true;
          removeMessages.push(await errorText(err, DELETE_FAILED));
        }
      }
      const uploaded = [];   // 올라간 첨부 행(서버 응답) — 실패했을 때 기존 칩으로 바꿔 보인다
      let uploadedCount = 0;
      const uploadMessages = [];
      const left = emptyStaged();
      for (const name of ATTACH_FIELDS) {
        for (const file of staged[name]) {
          if (!mountedRef.current) return;
          const formData = new FormData();
          formData.append('file', file);
          formData.append('field', name);
          try {
            const r = await devreqAPI.upload(targetId, formData);
            uploadedCount += 1;
            if (r?.data && r.data.id !== undefined && r.data.id !== null) uploaded.push({ ...r.data, field: r.data.field ?? name });
          } catch (err) {
            if (!err?.response) lost = true;
            left[name].push(file);
            uploadMessages.push(await errorText(err, UPLOAD_FAILED));
          }
        }
      }
      if (!mountedRef.current) return;
      if (removedIds.length > 0 || uploadedCount > 0) wrote = true;
      if (removeMessages.length === 0 && uploadMessages.length === 0) {
        notifySaved(latest);
        return;
      }
      // 실패 — 창을 닫지 않는다. 올라간 것은 기존 칩으로 바꾸고 실패한 칩만 남긴다.
      setServerFiles((prev) => [...prev.filter((f) => !removedIds.includes(f.id)), ...uploaded]);
      setRemovals((prev) => prev.filter((id) => !removedIds.includes(id)));
      setStaged(left);
      if ((wrote || lost) && latest) setCommitted(latest);
      const parts = [];
      if (uploadMessages.length > 0) parts.push(`첨부 ${uploadMessages.length}개를 올리지 못했습니다: ${uniq(uploadMessages).join(' · ')}`);
      if (removeMessages.length > 0) parts.push(`첨부 ${removeMessages.length}개를 지우지 못했습니다: ${uniq(removeMessages).join(' · ')}`);
      setError(`요청은 저장됐습니다. ${parts.join(' ')}`);
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  // 무엇이든 서버에 들어간 뒤에는 닫아도 onSaved(목록 새로고침 · 그 요청 상세 열기). 이미 알렸으면 아무것도 부르지 않는다.
  const requestClose = () => {
    if (savingRef.current || notifiedRef.current) return;
    if (committed) notifySaved(committed);
    else onClose?.();
  };

  const labelFor = (name, label, required) => (
    <label htmlFor={fieldId(name)} style={labelStyle}>
      {label}
      {required && <span aria-hidden="true" style={{ color: COLORS.dangerText }}> *</span>}
    </label>
  );

  const textField = ({ name, label, max, required, multiline, placeholder }, index) => {
    const body = (
      <>
        {labelFor(name, label, required)}
        {multiline ? (
          <textarea
            id={fieldId(name)}
            ref={areaRefs[name]}
            aria-label={label}
            value={values[name]}
            maxLength={max}
            rows={3}
            placeholder={placeholder}
            onChange={(e) => setField(name, e.target.value)}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        ) : (
          <input
            id={fieldId(name)}
            ref={index === 0 ? firstFieldRef : undefined}
            type="text"
            aria-label={label}
            value={values[name]}
            maxLength={max}
            placeholder={placeholder}
            onChange={(e) => setField(name, e.target.value)}
            style={inputStyle}
          />
        )}
        <div style={{ ...noteStyle, textAlign: 'right' }}>{`${commas(values[name].length)}/${commas(max)}`}</div>
      </>
    );
    if (!multiline) return <div key={name} style={boxStyle}>{body}</div>;
    return (
      <DevRequestAttachField
        key={name}
        label={label}
        files={existingOf(name)}
        staged={staged[name]}
        onStage={(list) => stageFiles(name, list)}
        onUnstage={(i) => unstage(name, i)}
        onRemoveExisting={markRemoval}
        otherCount={otherCountOf(name)}
        textareaRef={areaRefs[name]}
        disabled={attachOff}
        thumbs={thumbs}
        style={boxStyle}
      >
        {body}
      </DevRequestAttachField>
    );
  };

  const deptField = () => {
    if (isEdit) {
      return (
        <div style={boxStyle}>
          <div style={labelStyle}>부서</div>
          <div style={{ fontSize: '14px', color: COLORS.text }}>{item?.dept_name || '—'}</div>
        </div>
      );
    }
    return (
      <div style={boxStyle}>
        {myDepts.length >= 2 ? (
          <>
            {labelFor('dept', '부서', true)}
            <select id={fieldId('dept')} aria-label="요청 부서" value={deptChoice} disabled={savedId !== null}
              onChange={(e) => setDeptChoice(e.target.value)} style={inputStyle}>
              {myDepts.map((d) => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
            </select>
          </>
        ) : (
          <>
            <div style={labelStyle}>부서</div>
            <div style={{ fontSize: '14px', color: COLORS.text }}>{myDepts[0]?.name || '—'}</div>
          </>
        )}
        {deptItems !== null && cap > 0 && (
          <div style={noteStyle}>
            <span>{`이 부서 ${capCount}/${cap}건`}</span>
            {capCount >= cap && <span style={{ marginLeft: '6px', color: COLORS.dangerText }}>상한에 닿았습니다</span>}
          </div>
        )}
      </div>
    );
  };

  const titleText = isEdit ? `요청 고치기 · ${item?.req_no || ''}` : '새 요청';

  return (
    <div
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}
      // 창 둘레 배경에 놓은 파일도 무시한다(form 과 같다 — 브라우저가 그 파일로 탭을 옮기지 않게).
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => { if (dropHasFiles(event)) event.preventDefault(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px', background: 'rgba(17,24,39,.45)',
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="devreq-form-title"
        noValidate
        onSubmit={submit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            requestClose();
          }
        }}
        // 칸 상자 밖에 놓은 파일은 무시한다(브라우저가 그 파일로 탭을 옮겨 쓰던 창이 사라지지 않게).
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => { if (dropHasFiles(event)) event.preventDefault(); }}
        style={{
          width: 'min(640px, 100%)', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box',
          padding: '22px', borderRadius: RADIUS.lg, background: COLORS.surface, boxShadow: '0 18px 48px rgba(17,24,39,.2)',
        }}
      >
        <h2 id="devreq-form-title" style={{ margin: '0 0 4px', color: COLORS.text, fontSize: '18px', overflowWrap: 'anywhere' }}>{titleText}</h2>
        <p style={{ margin: '0 0 16px', color: COLORS.textSub, fontSize: '13px' }}>{FORM_GUIDE}</p>

        {filesState !== 'ready' && (
          filesState === 'failed' ? (
            <div role="alert" style={{ ...boxStyle, padding: '8px 12px', borderRadius: RADIUS.md, background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '13px' }}>
              {FILES_LOAD_FAILED}
            </div>
          ) : (
            <div style={{ ...boxStyle, color: COLORS.textMuted, fontSize: '13px' }}>{FILES_LOADING}</div>
          )
        )}

        {deptField()}
        {TEXT_FIELDS.map(textField)}

        <div style={boxStyle}>
          {labelFor('dept_rank', '부서 내 순위', false)}
          <select id={fieldId('dept_rank')} aria-label="부서 내 순위" value={values.dept_rank} disabled={rankLocked}
            onChange={(e) => setField('dept_rank', e.target.value)} style={inputStyle}>
            <option value="">미정</option>
            {rankOptions.map((n) => <option key={n} value={String(n)}>{n}</option>)}
          </select>
          {rankLocked && <div style={noteStyle}>{RANK_LOCKED}</div>}
        </div>

        {/* 공개 범위 — 읽기 전용 표시(고르는 칸 없음). 새 요청 = 회차 기본값 · 고치기 = 그 요청의 값. */}
        <div data-testid="devreq-form-visibility" style={{ ...boxStyle, padding: '10px 12px', borderRadius: RADIUS.md, background: '#f3f4f6' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: COLORS.text }}>{visibilityText(visibility)}</div>
          {visibility === 'private' && <div style={noteStyle}>요청 부서와 AX만 내용을 봅니다</div>}
        </div>

        {error && (
          <div role="alert" style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: RADIUS.md, background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '13px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={requestClose} disabled={saving} style={btn('secondary')}>{committed ? '닫기' : '취소'}</button>
          <button type="submit" disabled={!canSave} style={{ ...btn('primary'), opacity: canSave ? 1 : 0.5 }}>
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </form>
    </div>
  );
}
